import { ROUTES } from '../../constants/routes';
import {
  isAllowlistedFoundersOrigin,
  type ResolveCrossAppReturnHrefOptions,
  resolveCrossAppReturnHref,
} from './founders-member-hosts';

/** Pricing preselects installments; only the one-time plan needs a query value. */
export const MBA_ONE_TIME_PLAN_PARAM = 'one_time';

interface BuildPricingHrefOptions {
  /** Plan the visitor already chose, kept through sign-in so pricing opens on it */
  plan?: typeof MBA_ONE_TIME_PLAN_PARAM;
  /** Safe internal path to return to after checkout */
  returnTo?: string;
  /** Allowlisted founders landing origin for cross-app back navigation */
  returnOrigin?: string;
  /** Content title shown in the pricing selection card */
  courseTitle?: string;
}

export interface MbaReturnNavigation {
  href: string;
  label: string;
}

/**
 * Builds the member pricing checkout URL with optional return and course context.
 */
export function buildPricingHref({ plan, returnTo, returnOrigin, courseTitle }: BuildPricingHrefOptions = {}): string {
  const params = new URLSearchParams();

  if (plan) {
    params.set('plan', plan);
  }

  if (returnTo) {
    params.set('return_to', returnTo);
  }

  if (returnOrigin && isAllowlistedFoundersOrigin(returnOrigin)) {
    params.set('return_origin', returnOrigin);
  }

  const trimmedCourseTitle = courseTitle?.trim();
  if (trimmedCourseTitle) {
    params.set('course_title', trimmedCourseTitle);
  }

  const query = params.toString();
  return query ? `${ROUTES.PRICING}?${query}` : ROUTES.PRICING;
}

const MBA_RETURN_LABELS: Readonly<Record<string, string>> = {
  [ROUTES.MY_MBA]: 'Back to My MBA',
  [ROUTES.BILLING]: 'Back to Billing',
  [ROUTES.DASHBOARD]: 'Back to dashboard',
  [ROUTES.COURSES]: 'Back to courses',
};

/**
 * Resolves MBA/pricing back navigation labels for known member return paths.
 */
export function deriveMbaReturnNavigation(
  safeReturnPath: string | undefined,
  defaultHref: string = ROUTES.COURSES,
  crossAppOptions: ResolveCrossAppReturnHrefOptions = {},
): MbaReturnNavigation {
  const path = safeReturnPath ?? defaultHref;
  const href = safeReturnPath ? resolveCrossAppReturnHref(safeReturnPath, crossAppOptions) : defaultHref;

  return { href, label: MBA_RETURN_LABELS[path] ?? 'Go back' };
}
